import { Button, DialogDisclosure } from "@ariakit/react";
import {
  DialogTrigger,
  IndieTabletopClubLogo,
  SyncLog,
  useAppActions,
  useAppConfig,
  useCurrentUser,
  useIsAuthenticated,
  useSessionInfo,
} from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { Link, useLocation } from "wouter";
import { href } from "../App/href.tsx";
import { useUnlocksResult } from "../CheckAccess/context.ts";
import { ConfirmDialog } from "../ConfirmDialog/ConfirmDialog.tsx";
import { button } from "../common.css.ts";
import { database } from "../database/database.ts";
import { conjunction, FeatureEnum } from "../fmt.ts";
import {
  Page,
  SectionContainer,
  SectionContent,
  SectionHeading,
  SectionParagraph,
  ViewContent,
} from "../oss/Layout/Layout.tsx";
import { section } from "./style.css.ts";

function ClearAllDataDialog() {
  return (
    <ConfirmDialog
      heading="Delete all data?"
      description="Are you sure you want to delete all locally stored data? There is no way to undo this."
      submitLabel="Delete all"
      submitTheme="red"
      onSubmit={async () => {
        const result = await database.clearAll();
        return result.mapSuccess(() => ({ message: "Local data cleared." }));
      }}
    />
  );
}

function PoweredByITC() {
  return (
    <div className={section.itcPowered}>
      <div>Powered by</div>
      <IndieTabletopClubLogo className={section.itcLogo} />
    </div>
  );
}

function AccountSection() {
  const { logout } = useAppActions();
  const currentUser = useCurrentUser();
  const sessionInfo = useSessionInfo();
  const { fmt } = useAppConfig();
  const unlocksResult = useUnlocksResult();
  const [path] = useLocation();

  if (currentUser) {
    return (
      <SectionContainer>
        <SectionHeading>Account</SectionHeading>

        <SectionContent>
          <SectionParagraph>
            Logged in as: {currentUser.email} (
            <Link
              href={href("~/change-email", {
                search: { backTo: path, redirectTo: path },
              })}
              className={interactiveText}
            >
              change email
            </Link>
            )
            {sessionInfo && (
              <>
                <br />
                Session started: {fmt.dateTime(sessionInfo.createdTs)}
                <br />
                Expires: {fmt.dateTime(sessionInfo.expiresTs)}
              </>
            )}
            <br />
            Status:{" "}
            {currentUser.isVerified ? "Verified" : "Pending verification"}
            <br />
            Unlocked:{" "}
            {unlocksResult.unpack(
              ({ unlocks }) => {
                return unlocks.length > 0
                  ? conjunction(
                      unlocks
                        .map((unlock) => unlock.feature)
                        .filter((feature) => FeatureEnum.has(feature))
                        .map((feature) => FeatureEnum.get(feature)),
                    )
                  : "None";
              },
              () => "Failed to check unlocks",
              () => "Checking unlocks",
            )}
          </SectionParagraph>

          <div className={section.actions}>
            <Button className={button()} onClick={() => logout()}>
              Log out
            </Button>
          </div>
          <PoweredByITC />
        </SectionContent>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionHeading>Account</SectionHeading>

      <SectionContent>
        <SectionParagraph>
          Not logged in. Join Indie Tabletop Club or log into your account to
          unlock your owned content and enable backup & sync!
        </SectionParagraph>

        <div className={section.actions}>
          <Link
            className={button()}
            href={href("~/join", { search: { backTo: href("~/settings") } })}
          >
            Join
          </Link>
          <Link
            className={button()}
            href={href("~/login", { search: { backTo: href("~/settings") } })}
          >
            Log in
          </Link>
        </div>

        <PoweredByITC />
      </SectionContent>
    </SectionContainer>
  );
}

function DangerZoneSection() {
  const isAuthenticated = useIsAuthenticated();
  const currentUser = useCurrentUser();

  // This section is only available for users that are logged out. For logged
  // in users it would make a mess in syncing.
  if (currentUser || isAuthenticated) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeading>Danger Zone</SectionHeading>

      <SectionContent>
        <SectionParagraph>Delete all locally stored data.</SectionParagraph>

        <div className={section.actions}>
          <DialogTrigger>
            <ClearAllDataDialog />
            <DialogDisclosure className={button({ theme: "red" })}>
              Delete all
            </DialogDisclosure>
          </DialogTrigger>
        </div>
      </SectionContent>
    </SectionContainer>
  );
}

export function SettingsPage() {
  return (
    <Page backHref="~/" title="Settings">
      <ViewContent inlineVariant="narrow" blockVariant="shrink">
        <AccountSection />

        <SectionContainer>
          <SectionHeading>Sync Log</SectionHeading>
          <SectionContent>
            <SyncLog />
          </SectionContent>
        </SectionContainer>

        <DangerZoneSection />
      </ViewContent>
    </Page>
  );
}
